import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { APP_ROUTES } from '@routes/routes.constants';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { ResourceLookupPageProps } from './ResourceLookupPage.types';

/**
 * ResourceLookupPage - MFE wrapper for Resource Lookup
 *
 * Loads `schedule_mfe/ResourceLookupPage` and passes the MSAL instance.
 * The MFE handles its own internal routing.
 *
 * Guard stack (applied in instruct.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. PermissionGuard - Resources_View_MyLocations required
 *
 * @see docs/ROUTE_ARCHITECTURE.md for detailed documentation
 */

const ResourceLookupPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const { permissions } = usePermissions();
  const authData = useAuthenticatedUser();
  const navigate = useNavigate();

  const [RemoteRouter, setRemoteRouter] =
    React.useState<React.ComponentType<ResourceLookupPageProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  const navigateToScheduleDetails = React.useCallback(
    (id: string): void => {
      navigate(APP_ROUTES.INSTRUCT_MY_SCHEDULE_DETAIL.replace(':id', id));
    },
    [navigate]
  );

  React.useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      try {
        const { default: Router } =
          await import('schedule_mfe/ResourceLookupPage');
        setRemoteRouter(() => Router);
      } catch (error) {
        console.error('Error loading Resource lookup: ', error);
        setLoadError(error as Error);
      }
    };

    loadRouter();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteRouter) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteRouter
        msalInstance={instance}
        permissions={permissions}
        user={authData}
        onDetailsOpen={navigateToScheduleDetails}
      />
    </React.Suspense>
  );
});

ResourceLookupPage.displayName = 'ResourceLookupPage';

export { ResourceLookupPage };
